import { Controller } from "@hotwired/stimulus"

/**
 * WeekStripController — mobile week navigation for staff schedule.
 *
 * Handles:
 * - Previous / next week navigation via Turbo visits
 * - Day tap → scroll to day section
 * - "Vandaag" button → scroll to today section
 * - Touch swipe left/right for week changes
 */
export default class extends Controller {
  static targets = ["strip", "dayButton", "weekLabel"]
  static values  = {
    anchorDate: String,      // ISO date for current week anchor
    baseUrl:    String       // base URL for week navigation
  }

  connect() {
    this._touchStartX = null
    this._touchStartY = null
    this.element.addEventListener("touchstart", this._onTouchStart.bind(this), { passive: true })
    this.element.addEventListener("touchend", this._onTouchEnd.bind(this), { passive: true })
  }

  disconnect() {
    this.element.removeEventListener("touchstart", this._onTouchStart.bind(this))
    this.element.removeEventListener("touchend", this._onTouchEnd.bind(this))
  }

  // Navigate to previous week
  previousWeek() {
    const anchor = this._parseAnchor()
    anchor.setDate(anchor.getDate() - 7)
    this._navigateToWeek(anchor)
  }

  // Navigate to next week
  nextWeek() {
    const anchor = this._parseAnchor()
    anchor.setDate(anchor.getDate() + 7)
    this._navigateToWeek(anchor)
  }

  // Navigate to current week (today)
  goToToday() {
    const today = new Date()
    this._navigateToWeek(today)
    // After navigation, scroll to today's section
    requestAnimationFrame(() => {
      const iso = today.toISOString().split("T")[0]
      const todaySection = document.getElementById(`day-${iso}`)
      if (todaySection) {
        todaySection.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    })
  }

  // Scroll to a specific day section when day button is tapped
  scrollToDay(event) {
    const dateStr = event.currentTarget.dataset.date
    const section = document.getElementById(`day-${dateStr}`)
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" })
    }

    // Update active state
    this.dayButtonTargets.forEach(btn => {
      btn.classList.remove("bg-teal-500", "text-white")
      btn.classList.add("text-gray-700")
    })
    event.currentTarget.classList.add("bg-teal-500", "text-white")
    event.currentTarget.classList.remove("text-gray-700")
  }

  // --- Private ---

  _parseAnchor() {
    const parts = this.anchorDateValue.split("-")
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
  }

  _navigateToWeek(date) {
    const iso = date.toISOString().split("T")[0]
    const url = `${this.baseUrlValue}?week=${iso}`
    Turbo.visit(url, { action: "replace" })
  }

  _onTouchStart(event) {
    if (event.touches.length !== 1) return
    this._touchStartX = event.touches[0].clientX
    this._touchStartY = event.touches[0].clientY
  }

  _onTouchEnd(event) {
    if (this._touchStartX === null) return
    const touch = event.changedTouches[0]
    const dx = touch.clientX - this._touchStartX
    const dy = touch.clientY - this._touchStartY

    // Only trigger on horizontal swipes (not vertical scroll)
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) {
        this.nextWeek()      // Swipe left → next week
      } else {
        this.previousWeek()  // Swipe right → previous week
      }
    }

    this._touchStartX = null
    this._touchStartY = null
  }
}
